import React, { createContext, useContext, useState, useCallback } from 'react';
import './Toast.css';


const TOAST_Z_INDEX = 9999;
const MODAL_Z_INDEX = 10000;

const ToastContext = createContext(null);

let idCounter = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState({ visible: false });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const removeToast = useCallback((id) => {
    setToasts((t) => t.filter(x => x.id !== id));
  }, []);

  const showToast = useCallback(({ message, type = 'info', duration = 4000, actions = [] }) => {
    const id = idCounter++;
    const toast = { id, message, type, actions };
    setToasts((t) => [...t, toast]);

    if (!actions || actions.length === 0) {
      setTimeout(() => removeToast(id), duration);
    }

    return id;
  }, [removeToast]);

  // show confirm returns a promise that resolves to true/false
  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        visible: true,
        title: options.title,
        message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        onConfirm: async () => {
          if (options.onConfirm) {
            setConfirmLoading(true);
            try {
              await options.onConfirm();
              resolve(true);
            } catch (error) {
              showToast({ message: error.message || 'Operation failed', type: 'error' });
              resolve(false);
            } finally {
              setConfirmLoading(false);
              setConfirmState({ visible: false });
            }
          } else {
            setConfirmState({ visible: false });
            resolve(true);
          }
        },
        onCancel: () => {
          setConfirmState({ visible: false });
          resolve(false);
        }
      });
    });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showConfirm }}>
      {children}
      <div className="toast-root" style={{ zIndex: TOAST_Z_INDEX }}>
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <div className="toast-message">{t.message}</div>
            {t.actions && t.actions.length > 0 && (
              <div className="toast-actions">
                {t.actions.map((a, i) => (
                  <button
                    key={i}
                    className={`toast-btn ${a.primary ? 'primary' : ''}`}
                    onClick={() => a.onClick && a.onClick()}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Centered confirm modal */}
      {confirmState.visible && (
        <div className="cm-overlay" style={{ zIndex: MODAL_Z_INDEX }}>
          <div className="cm-box">
            {confirmState.title && <h3 className="cm-title">{confirmState.title}</h3>}
            <p className="cm-message">{confirmState.message}</p>
            <div className="cm-actions">
              <button 
                className="cm-btn cm-cancel" 
                onClick={confirmState.onCancel}
                disabled={confirmLoading}
              >
                {confirmState.cancelText}
              </button>
              <button 
                className="cm-btn cm-confirm" 
                onClick={confirmState.onConfirm}
                disabled={confirmLoading}
              >
                {confirmLoading ? 'Please wait...' : confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

export default ToastContext;
